import "./LegalPage.css";

const LEGAL_PAGES = {
  privacy: {
    title: "Privacy Policy",
    intro:
      "At Dwellora Designs, we respect your privacy and are committed to protecting the personal information you share with us.",
    sections: [
      {
        title: "Information We Collect",
        paragraphs: [
          "When you contact us, request a consultation, submit an enquiry, or interact with our website, we may collect information such as your name, phone number, email address, project details, and other information you choose to provide.",
          "We may also collect basic technical information such as browser type, device information, and website usage data to help us improve your experience.",
        ],
      },
      {
        title: "How We Use Your Information",
        intro: "We may use your information to:",
        points: [
          "Respond to your enquiries and requests",
          "Discuss your interior design or project requirements",
          "Schedule consultations",
          "Provide project-related information and updates",
          "Improve our website, services, and customer experience",
          "Communicate with you regarding relevant services",
        ],
      },
      {
        title: "Information Sharing",
        paragraphs: [
          "Dwellora Designs does not sell or rent your personal information. Your information may only be shared with trusted service providers or partners when necessary to provide our services or operate our website.",
        ],
      },
      {
        title: "Data Security",
        paragraphs: [
          "We take reasonable measures to protect the information shared with us from unauthorized access, misuse, alteration, or disclosure.",
        ],
      },
      {
        title: "Your Choices",
        paragraphs: [
          "You may contact us if you wish to update, correct, or request deletion of your personal information, subject to applicable legal requirements.",
        ],
      },
      {
        title: "Updates to This Policy",
        paragraphs: [
          "We may update this Privacy Policy from time to time to reflect changes in our services, technology, or legal requirements.",
          "For questions regarding this Privacy Policy, please contact Dwellora Designs through the contact details provided on our website.",
        ],
      },
    ],
  },
  help: {
    title: "Help & Support",
    intro:
      "Need help with your project or have a question about Dwellora Designs? We're here to help.",
    sections: [
      {
        title: "How Can We Help?",
        subsections: [
          {
            title: "Interior Design Enquiries",
            body: "Tell us about your space, requirements, style preferences, and project goals. Our team can help you understand the next steps.",
          },
          {
            title: "Project Consultation",
            body: "If you're planning a new home, renovation, or interior upgrade, you can get in touch with us to discuss your requirements and explore suitable design solutions.",
          },
          {
            title: "Project Information",
            body: "For questions about design, materials, furniture, execution, timelines, or other project-related details, please reach out to our team.",
          },
          {
            title: "Website Enquiries",
            body: "If you experience an issue while using our website or submitting an enquiry, please contact us with a brief description of the problem.",
          },
        ],
      },
      {
        title: "Contact Us",
        paragraphs: [
          "For assistance, please use the contact details or enquiry form available on the Dwellora Designs website.",
          "We aim to respond to enquiries as soon as possible.",
        ],
      },
    ],
  },
  cookies: {
    title: "Cookies Policy",
    intro:
      "Dwellora Designs uses cookies and similar technologies to provide a better, more reliable, and secure website experience.",
    sections: [
      {
        title: "What Are Cookies?",
        paragraphs: [
          "Cookies are small text files stored on your device when you visit a website. They help websites remember information about your visit and understand how visitors use the website.",
        ],
      },
      {
        title: "How We Use Cookies",
        intro: "Cookies may be used to:",
        points: [
          "Keep the website functioning properly",
          "Improve website performance",
          "Understand how visitors interact with our website",
          "Remember certain preferences",
          "Improve our services and user experience",
        ],
      },
      {
        title: "Third-Party Services",
        paragraphs: [
          "Some website features or analytics tools may use cookies or similar technologies provided by third-party services. These services may process information according to their own privacy policies.",
        ],
      },
      {
        title: "Managing Cookies",
        paragraphs: [
          "You can manage or disable cookies through your browser settings. Please note that disabling certain cookies may affect the functionality or performance of parts of the website.",
        ],
      },
      {
        title: "Changes to This Policy",
        paragraphs: [
          "We may update this Cookies Policy when our website, technology, or services change. Any updates will be published on this page.",
        ],
      },
    ],
  },
  credits: {
    title: "Credits",
    intro:
      "The Dwellora Designs website is created to showcase our approach to thoughtful interior design, carefully considered spaces, and quality execution.",
    sections: [
      {
        title: "Design & Development",
        paragraphs: [
          "Website design and development by the Dwellora Designs team and its creative and technology partners.",
        ],
      },
      {
        title: "Photography & Visuals",
        paragraphs: [
          "Project imagery and visual content are used to represent Dwellora Designs' design approach, materials, spaces, and services.",
          "Where third-party imagery, fonts, icons, software, or other resources are used, credit remains with their respective creators and licensors.",
        ],
      },
      {
        title: "Content",
        paragraphs: [
          "All original brand content, project descriptions, design concepts, and website copy created for Dwellora Designs remain the property of Dwellora Designs unless otherwise stated.",
        ],
      },
      {
        title: "Thank You",
        paragraphs: [
          "Thank you to our clients, collaborators, craftsmen, designers, and partners who contribute to bringing every Dwellora Designs project to life.",
        ],
      },
    ],
  },
};

export default function LegalPage({ page }) {
  const content = LEGAL_PAGES[page];

  if (!content) return null;

  return (
    <article className="legal-page">
      <div className="legal-page-wrap">
        <header className="legal-page-header">
          <p className="legal-page-eyebrow">Dwellora Designs</p>
          <h1>{content.title}</h1>
          <p className="legal-page-intro">{content.intro}</p>
        </header>

        <div className="legal-page-content">
          {content.sections.map((section) => (
            <section className="legal-page-section" key={section.title}>
              <h2>{section.title}</h2>
              {section.intro && <p>{section.intro}</p>}
              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.points && (
                <ul>
                  {section.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              )}
              {section.subsections?.map((subsection) => (
                <div className="legal-page-subsection" key={subsection.title}>
                  <h3>{subsection.title}</h3>
                  <p>{subsection.body}</p>
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
    </article>
  );
}
